import React from 'react';
import { Controller, Control, UseFormWatch } from 'react-hook-form';
import { addDays, format } from 'date-fns';
import { useCollaboratorAllocationsQuery } from '@/features/Collaborators/Allocations/data';
import { AllocationStatuses, AllocationTypes, CollaboratorAllocation } from '@/features/Collaborators/Models';
import { normalizeActivityStatus } from '@/utils/collaborator-status';

interface TerminationFieldsProps {
    collaboratorId?: string;
    control: Control<any>;
    watch: UseFormWatch<any>;
    isSubmitting?: boolean;
}

const startOfToday = (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const findPendingNotice = (allocations: CollaboratorAllocation[]): CollaboratorAllocation | null => {
    const today = startOfToday();
    const pending = allocations
        .filter((allocation) => allocation.allocationType === AllocationTypes.Product)
        .filter((allocation) => allocation.status !== AllocationStatuses.Cancelled && !allocation.terminationAppliedAt)
        .filter((allocation) => allocation.endDate && allocation.endDate.toDate() >= today)
        .sort((a, b) => a.endDate!.toMillis() - b.endDate!.toMillis());

    return pending[0] ?? null;
};

export const TerminationFields: React.FC<TerminationFieldsProps> = ({
    collaboratorId,
    control,
    watch,
    isSubmitting = false,
}) => {
    const { data: allocations = [] } = useCollaboratorAllocationsQuery(collaboratorId);
    const status = watch('status');
    const terminationAllocationId = watch('terminationAllocationId');
    const terminationReason = watch('terminationReason');

    const isTerminated = normalizeActivityStatus(status) === 'desligado';
    const pendingNotice = findPendingNotice(allocations);
    const showReason = isTerminated || Boolean(terminationReason);

    return (
        <div className="space-y-4">
            {!isTerminated && pendingNotice?.endDate && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Em aviso prévio até {format(pendingNotice.endDate.toDate(), 'dd/MM/yyyy')}. Em{' '}
                    {format(addDays(pendingNotice.endDate.toDate(), 1), 'dd/MM/yyyy')} o colaborador será marcado como
                    Desligado automaticamente.
                </div>
            )}

            {showReason && (
                <div>
                    <label htmlFor="terminationReason" className="block text-sm font-medium text-gray-700">
                        Motivo do desligamento
                    </label>
                    <Controller
                        name="terminationReason"
                        control={control}
                        render={({ field }) => (
                            <textarea
                                {...field}
                                value={field.value ?? ''}
                                id="terminationReason"
                                rows={2}
                                maxLength={500}
                                disabled={isSubmitting}
                                placeholder="Ex.: pedido de demissão, término de contrato"
                                className="input-primary mt-1 w-full"
                            />
                        )}
                    />
                    {terminationAllocationId ? (
                        <p className="mt-1 text-xs text-gray-500">Preenchido automaticamente pelo aviso prévio.</p>
                    ) : (
                        <p className="mt-1 text-xs text-gray-500">Opcional.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default TerminationFields;
